import { Play } from "lucide-react";
import { useEffect, useState } from "react";

interface VideoPlaceholderProps {
  /**
   * Drop a video URL in here and this component renders a real player
   * instead of the branded placeholder. That single prop is the only change
   * needed to go live, e.g. <VideoPlaceholder src="/media/how-it-works.mp4" />
   */
  src?: string;
  /** YouTube/Vimeo embed URL. When set, renders a real 16:9 iframe. */
  embedUrl?: string;
  /** Optional 9:16 Gumlet embed; only the selected player is mounted. */
  embedUrlMobile?: string;
  /** Optional still image shown before the video plays. */
  poster?: string;
  /** Accessible label / caption for the video. */
  label?: string;
}

export function VideoPlaceholder({
  src,
  embedUrl,
  embedUrlMobile,
  poster,
  label = "How Leadership Edge works",
}: VideoPlaceholderProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    if (!embedUrlMobile) return;
    const media = window.matchMedia("(min-width: 768px)");
    const update = () => setIsMobile(!media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [embedUrlMobile]);

  // No iframe in SSR or the first hydration render: the unselected player
  // cannot make a request before the browser's breakpoint is known.
  if (embedUrlMobile && isMobile === null) {
    return (
      <div
        aria-hidden="true"
        className="relative mx-auto aspect-[9/16] max-h-[80svh] w-[min(100%,calc(80svh*9/16))] overflow-hidden rounded-2xl border border-border/60 bg-ink shadow-brand md:aspect-video md:max-h-none md:w-full"
      />
    );
  }

  const portrait = Boolean(embedUrlMobile && isMobile);
  const selectedEmbedUrl = portrait ? embedUrlMobile : embedUrl;

  if (selectedEmbedUrl && /^https:\/\/play\.gumlet\.io\//.test(selectedEmbedUrl)) {
    // Gumlet's required embed attributes; same frame styling as YouTube below.
    return (
      <div className={portrait
        ? "mx-auto max-h-[80svh] w-[min(100%,calc(80svh*9/16))] overflow-hidden rounded-2xl border border-border/60 bg-ink shadow-brand"
        : "overflow-hidden rounded-2xl border border-border/60 bg-ink shadow-brand"}>
        <div className={portrait ? "relative aspect-[9/16] w-full" : "relative aspect-video w-full"}>
          <iframe
            key={selectedEmbedUrl}
            src={selectedEmbedUrl}
            title={label}
            loading="lazy"
            referrerPolicy="origin"
            allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen; clipboard-write;"
            allowFullScreen
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
      </div>
    );
  }

  if (embedUrl) {
    return (
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-ink shadow-brand">
        <iframe
          src={embedUrl}
          title={label}
          loading="lazy"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="aspect-video h-auto w-full"
        />
      </div>
    );
  }

  if (src) {
    return (
      <div className="overflow-hidden rounded-2xl border border-border/60 bg-ink shadow-brand">
        <video
          controls
          preload="metadata"
          poster={poster}
          aria-label={label}
          className="aspect-video h-auto w-full"
        >
          <source src={src} />
        </video>
      </div>
    );
  }

  // No src yet: branded 16:9 placeholder.
  return (
    <div
      className="relative flex aspect-video w-full items-center justify-center overflow-hidden rounded-2xl border border-primary/20"
      style={{
        background:
          "linear-gradient(150deg, #EFECF4 0%, #DFD8E9 45%, color-mix(in oklab, var(--primary) 25%, transparent) 100%)",
      }}
    >
      <div className="flex flex-col items-center gap-4 text-center">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#693D94] text-white shadow-brand">
          <Play className="h-6 w-6 translate-x-0.5" aria-hidden="true" />
        </span>
        <p
          style={{ fontFamily: "'Poppins', sans-serif" }}
          className="text-sm font-medium text-ink/70"
        >
          Video coming soon
        </p>
      </div>
    </div>
  );
}
